import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Business as BusinessIcon,
  Category as CategoryIcon,
  ReceiptLong as ReceiptIcon,
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  School as SchoolIcon,
  BarChart as ChartIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";

const ACCENT = "#059669";
const ACCENT_LIGHT = "#10b981";
const ACCENT_DARK = "#047857";

const STORAGE_KEY = "costControl_tutorialDismissed";

const tutorialStagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.08 },
  },
};

const tutorialFadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 280, damping: 24 },
  },
};

const NAV_LABELS = [
  "Notas Fiscais",
  "Notas Recebidas",
  "Categorias",
  "Fornecedores",
  "Dashboard",
];

const TUTORIAL_STEPS = [
  {
    icon: CategoryIcon,
    number: "01",
    title: "Crie categorias",
    description:
      "Comece cadastrando categorias de custo (ex: Alimentos, Embalagens, Transporte). Acesse a aba Categorias e clique em Nova Categoria.",
    tab: 2,
  },
  {
    icon: BusinessIcon,
    number: "02",
    title: "Cadastre fornecedores",
    description:
      "Registre seus parceiros comerciais com nome, contato e e-mail. Acesse Fornecedores e adicione cada um deles.",
    tab: 3,
  },
  {
    icon: ReceiptIcon,
    number: "03",
    title: "Lance notas fiscais",
    description:
      "Registre notas manualmente em Notas Fiscais, ou envie fotos pelo WhatsApp. Informe categoria, fornecedor, valor e data.",
    tab: 0,
  },
  {
    icon: FilterIcon,
    number: "04",
    title: "Filtre e exporte",
    description:
      "Use os filtros por categoria, fornecedor, datas e valores. Exporte os dados em CSV para analise externa.",
    tab: 0,
  },
  {
    icon: ChartIcon,
    number: "05",
    title: "Acompanhe o dashboard",
    description:
      "Visualize custos totais, medias, distribuicao por categoria e tendencias semanais em graficos interativos.",
    tab: 4,
  },
];

export function isTutorialDismissed() {
  return localStorage.getItem(STORAGE_KEY) === "true";
}

export function dismissTutorial() {
  localStorage.setItem(STORAGE_KEY, "true");
}

export default function TutorialModal({ open, onClose, onNavigateToTab }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    dismissTutorial();
    onClose();
  };

  const handleStepClick = (tab) => {
    onClose();
    onNavigateToTab(tab);
  };

  return (
    <AnimatePresence>
      {open && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={handleClose}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 5000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(3px)",
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: isMobile ? "95%" : "90%",
              maxWidth: 900,
              maxHeight: "90vh",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: theme.palette.background.paper,
              boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: `linear-gradient(135deg, #064e3b 0%, ${ACCENT_DARK} 50%, ${ACCENT} 100%)`,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 20, color: "#a7f3d0" }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: "0.85rem",
                      lineHeight: 1.3,
                    }}
                  >
                    Como usar o Custo Simples
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: alpha("#fff", 0.55), fontSize: "0.68rem" }}
                  >
                    Passo a passo completo para controlar seus custos
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ color: alpha("#fff", 0.6) }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Body */}
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 3,
                overflowY: "auto",
                flex: 1,
              }}
            >
              <motion.div
                variants={tutorialStagger}
                initial="hidden"
                animate="visible"
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      lg: "1fr 1fr 1fr",
                    },
                    gap: 2,
                  }}
                >
                  {TUTORIAL_STEPS.map((step, idx) => {
                    const StepIcon = step.icon;
                    return (
                      <motion.div
                        key={idx}
                        variants={tutorialFadeUp}
                        whileHover={{ y: -3 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <Box
                          onClick={() => handleStepClick(step.tab)}
                          sx={{
                            position: "relative",
                            p: 2.5,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(ACCENT, 0.12)}`,
                            background: theme.palette.background.paper,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: alpha(ACCENT, 0.3),
                              boxShadow: `0 4px 16px ${alpha(ACCENT, 0.08)}`,
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 14,
                              fontSize: "0.6rem",
                              fontWeight: 800,
                              letterSpacing: "1px",
                              color: alpha(ACCENT, 0.25),
                            }}
                          >
                            {step.number}
                          </Typography>

                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 1.5,
                              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(ACCENT_LIGHT, 0.15)} 100%)`,
                            }}
                          >
                            <StepIcon sx={{ fontSize: 20, color: ACCENT }} />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.82rem",
                              color: theme.palette.text.primary,
                              mb: 0.75,
                              lineHeight: 1.3,
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.72rem",
                              color: theme.palette.text.secondary,
                              lineHeight: 1.55,
                              flex: 1,
                            }}
                          >
                            {step.description}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 1.5,
                              pt: 1,
                              borderTop: `1px solid ${alpha(ACCENT, 0.08)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                color: ACCENT,
                              }}
                            >
                              Ir para {NAV_LABELS[step.tab]}
                            </Typography>
                            <ArrowIcon
                              sx={{ fontSize: 12, color: alpha(ACCENT, 0.5) }}
                            />
                          </Box>
                        </Box>
                      </motion.div>
                    );
                  })}
                </Box>

                {/* Tips */}
                <motion.div variants={tutorialFadeUp}>
                  <Box
                    sx={{
                      mt: 2.5,
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 2,
                    }}
                  >
                    <TipCard
                      label="Dica rapida:"
                      text="Comece criando as categorias e fornecedores antes de lancar notas. Isso facilita a organizacao e permite filtrar os dados corretamente no dashboard."
                    />
                    <TipCard
                      label="Bot WhatsApp:"
                      text="Autorize os numeros que podem enviar notas em Configuracoes > Bot de Notas WhatsApp. Fotografe a nota com boa iluminacao e envie pelo numero cadastrado — a IA extrai os dados e ja lanca no sistema."
                    />
                  </Box>
                </motion.div>
              </motion.div>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: "flex",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: ACCENT,
                  "&:hover": { bgcolor: ACCENT_DARK },
                }}
              >
                Entendi
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </AnimatePresence>
  );
}

function TipCard({ label, text }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        px: 2.5,
        py: 1.5,
        borderRadius: 2,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        background: alpha(ACCENT, 0.06),
        border: `1px dashed ${alpha(ACCENT, 0.2)}`,
      }}
    >
      <CheckIcon
        sx={{ fontSize: 18, color: ACCENT, mt: 0.25, flexShrink: 0 }}
      />
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.72rem",
          color: theme.palette.text.secondary,
          lineHeight: 1.55,
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: ACCENT_DARK }}>
          {label}
        </Box>{" "}
        {text}
      </Typography>
    </Box>
  );
}
